import { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * RangeHandle - A draggable teardrop handle for adjusting mark boundaries.
 */
interface RangeHandleProps {
    wordId: string;
    type: 'start' | 'end';
    onDragStart?: () => void;
    onDrag: (wordId: string) => void;
    onDragComplete?: () => void;
}

export function RangeHandle({ wordId, type, onDragStart, onDrag, onDragComplete }: RangeHandleProps) {
    const [anchor, setAnchor] = useState({ x: 0, y: 0 });
    const isDragging = useRef(false);
    const lastDraggedWordId = useRef<string | null>(null);


    useEffect(() => {
        if (isDragging.current) return;

        const element = document.getElementById(wordId);
        if (element) {
            const rect = element.getBoundingClientRect();
            const centerY = rect.top + rect.height / 2;
            setAnchor({
                x: type === 'start' ? rect.left : rect.right,
                y: centerY
            });
        }
    }, [wordId, type]);

    const handlePointerDown = (e: React.PointerEvent) => {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        isDragging.current = true;
        lastDraggedWordId.current = wordId;
        onDragStart?.();
    };

    const handleDrag = (_: any, info: any) => {
        const handles = document.querySelectorAll('.range-handle') as NodeListOf<HTMLElement>;
        handles.forEach(h => h.style.visibility = 'hidden');
        void document.body.offsetHeight;

        const element = document.elementFromPoint(info.point.x, info.point.y);
        const wordSpan = element?.closest('span[id^="w-"]');

        handles.forEach(h => h.style.visibility = '');

        if (wordSpan) {
            lastDraggedWordId.current = wordSpan.id;
            if (wordSpan.id !== wordId) {
                onDrag(wordSpan.id);
            }
        }
    };

    const handleDragEnd = () => {
        isDragging.current = false;
        lastDraggedWordId.current = null;

        const element = document.getElementById(wordId);
        if (element) {
            const rect = element.getBoundingClientRect();
            const centerY = rect.top + rect.height / 2;
            const newAnchor = {
                x: type === 'start' ? rect.left : rect.right,
                y: centerY
            };
            setAnchor(newAnchor);
        }

        // Normalize the mark (swap start/end if needed)
        onDragComplete?.();
    };

    return (
        <motion.div
            drag
            dragMomentum={false}
            dragElastic={0}
            dragSnapToOrigin={true}
            onPointerDown={handlePointerDown}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            className="range-handle"
            animate={{
                left: anchor.x,
                top: anchor.y,
            }}
            transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30,
            }}
            style={{
                position: 'fixed',
                width: 36,
                height: 36,
                zIndex: 19,
                cursor: 'grab',
                touchAction: 'none',
                translateX: type === 'start' ? -35 : -1, // Nudged 7px closer to the word
                translateY: -21, // Shifted 3px up
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: 'hsl(var(--primary) / 0.3)', // Very transparent center
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '2px solid hsl(var(--primary) / 0.8)', // Solid brand border
                boxShadow: '0 4px 16px rgba(0,0,0,0.25), inset 0 0 8px rgba(255,255,255,0.4)',
            }}
            initial={false}
        >
            {/* Grip lines (Horizontal) */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
            }}>
                <div style={{ width: 12, height: 2, backgroundColor: 'white', borderRadius: 1, opacity: 0.8 }} />
                <div style={{ width: 12, height: 2, backgroundColor: 'white', borderRadius: 1, opacity: 0.8 }} />
            </div>
        </motion.div>
    );
}
